import { useEffect, useCallback, useRef } from "react";
import { useAui, useAuiState } from "@assistant-ui/react";
import useLocalStorage from "use-local-storage-state";

interface DraftStorage {
  [contextId: string]: {
    [conversationId: string]: string;
  };
}

const LOCAL_STORAGE_KEY = "sc-agent-drafts";
const MAX_DRAFTS_PER_CONTEXT = 50;

interface UseDraftSyncOptions {
  contextId: string;
  conversationId: string | null;
  debounceMs?: number;
}

/**
 * Observes the assistant-ui composer text and persists it as a
 * per-conversation draft in localStorage.
 *
 * This hook owns NO text state — the assistant-ui composer is the
 * single source of truth. It is a pure side-effect observer.
 *
 * Each instance is keyed on conversationId (via the parent's React key),
 * so it restores the draft on mount and flushes on unmount.
 */
export function useDraftSync({
  contextId,
  conversationId,
  debounceMs = 300,
}: UseDraftSyncOptions): { clearDraft: () => void } {
  const aui = useAui();
  const composerText = useAuiState((s) => s.composer.text);

  const [storage, setStorage] = useLocalStorage<DraftStorage>(LOCAL_STORAGE_KEY, {
    defaultValue: {},
  });

  const safeSetStorage = useCallback(
    (updater: (prev: DraftStorage) => DraftStorage) => {
      try {
        setStorage(updater);
      } catch (error) {
        console.error("Failed to save draft to localStorage:", error);
      }
    },
    [setStorage]
  );

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const composerTextRef = useRef(composerText);
  composerTextRef.current = composerText;

  // --- Restore draft on mount ---

  useEffect(() => {
    if (!conversationId || !contextId) return;
    const saved = storage[contextId]?.[conversationId] ?? "";
    aui.composer().setText(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // --- Debounced save on text change ---

  useEffect(() => {
    if (!conversationId || !contextId) return;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;

      safeSetStorage((prev) => {
        const contextDrafts = { ...(prev[contextId] || {}) };

        if (!composerText.trim()) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [conversationId]: _, ...rest } = contextDrafts;
          return { ...prev, [contextId]: rest };
        }

        let cleaned = contextDrafts;
        if (Object.keys(cleaned).length >= MAX_DRAFTS_PER_CONTEXT) {
          const nonEmpty = Object.entries(cleaned).filter(([, v]) => v.trim() !== "");
          cleaned = Object.fromEntries(nonEmpty);
          if (Object.keys(cleaned).length >= MAX_DRAFTS_PER_CONTEXT) {
            cleaned = Object.fromEntries(
              Object.entries(cleaned).slice(-MAX_DRAFTS_PER_CONTEXT + 1)
            );
          }
        }

        return { ...prev, [contextId]: { ...cleaned, [conversationId]: composerText } };
      });
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [composerText, conversationId, contextId, debounceMs, safeSetStorage]);

  // --- Flush pending save on unmount ---

  const safeSetStorageRef = useRef(safeSetStorage);
  safeSetStorageRef.current = safeSetStorage;

  useEffect(() => {
    return () => {
      if (!debounceTimerRef.current) return;
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;

      if (conversationId && contextId && composerTextRef.current.trim()) {
        safeSetStorageRef.current((prev) => ({
          ...prev,
          [contextId]: { ...(prev[contextId] || {}), [conversationId]: composerTextRef.current },
        }));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount only
  }, []);

  // --- Clear draft ---

  const clearDraft = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (!conversationId || !contextId) return;

    safeSetStorage((prev) => {
      const contextDrafts = prev[contextId] || {};
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [conversationId]: _, ...rest } = contextDrafts;
      return { ...prev, [contextId]: rest };
    });
  }, [conversationId, contextId, safeSetStorage]);

  return { clearDraft };
}
